import React, { useMemo } from 'react';
import {
  ActionDropdown,
  ActionDropdownOption
} from 'components/ActionDropdown';
import { useNavigate } from 'react-router-dom';
import { useIntegration } from 'services/integration/hooks';
import { Paths } from 'services/navigation/paths';
import { appendSearchParamsToUrl } from 'utils/fomatting';
import { CAMPAIGN_CREATION_PARAMS } from 'services/campaign/constants';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import { useConfirmationModal } from 'services/modals/hooks';
import {
  useCampaignDialogsStore,
  CampaignDialogKey
} from 'modules/campaigns/stores/campaignDialogsStore';

export const StartCampaign = () => {
  const navigate = useNavigate();
  const { isConnected, isListsAvailable } = useIntegration();
  const { track } = useTracking();
  const { showModal } = useConfirmationModal();
  const campaignDialogStore = useCampaignDialogsStore();
  const { setDialogHidden } = campaignDialogStore;

  const createCampaign = () => {
    navigate(Paths.CAMPAIGN_CREATE_DRAFT);
  };

  const createCampaignFromCandidateList = () => {
    track(TrackingEventName.CampaignsListCreateFromCandidatesList);
    navigate(Paths.CAMPAIGN_CREATE_DRAFT, {
      state: { isFromCandidatesList: true }
    });
  };

  const createCampaignFromAutomation = () => {
    track(TrackingEventName.CampaignsListCreateForAutomation);
    navigate(
      appendSearchParamsToUrl(Paths.CAMPAIGN_CREATE_DRAFT, {
        from: CAMPAIGN_CREATION_PARAMS.automation
      })
    );
  };

  const campaignCreationActionsOptions = useMemo<ActionDropdownOption[]>(() => {
    const options: ActionDropdownOption[] = [];

    if (isConnected) {
      options.push(
        {
          label: 'For all applicants',
          onClick: () => {
            if (campaignDialogStore[CampaignDialogKey.ATS_FOR_ALL_CANDIDATES]) {
              track(TrackingEventName.CampaignsListCreateForRole);
              createCampaign();
              return;
            }

            showModal({
              showDontShowAgainCheckbox: true,
              title: 'Message all applicants?',
              message:
                'All applicants connected to a job in your ATS will be contacted.',
              confirmationText: 'Continue',
              onConfirm: (dontShowAgainCheck) => {
                if (dontShowAgainCheck) {
                  setDialogHidden(
                    CampaignDialogKey.ATS_FOR_ALL_CANDIDATES,
                    true
                  );
                }
                track(TrackingEventName.CampaignsListCreateForRole);
                createCampaign();
              }
            });
          }
        },
        {
          label: 'For applicants in an analysis',
          onClick: () => {
            if (
              campaignDialogStore[
                CampaignDialogKey.ATS_FOR_ALL_APPLICANTS_IN_ANALYSIS
              ]
            ) {
              createCampaignFromAutomation();
              return;
            }

            showModal({
              showDontShowAgainCheckbox: true,
              title: 'Connect to an analysis?',
              message:
                'Create a campaign to message some or all applicants who have already been analysed.',
              confirmationText: 'Continue',
              onConfirm: (dontShowAgainCheck) => {
                if (dontShowAgainCheck) {
                  setDialogHidden(
                    CampaignDialogKey.ATS_FOR_ALL_APPLICANTS_IN_ANALYSIS,
                    true
                  );
                }
                createCampaignFromAutomation();
              }
            });
          }
        }
      );

      if (isListsAvailable) {
        options.push({
          label: 'For passive candidates',
          onClick: () => {
            if (
              campaignDialogStore[CampaignDialogKey.ATS_FOR_PASSIVE_CANDIDATES]
            ) {
              createCampaignFromCandidateList();
              return;
            }

            showModal({
              showDontShowAgainCheckbox: true,
              title: 'Contact passive candidates?',
              message:
                'Choose a talent pool (ATS dependent) e.g. tearsheets, lists, groups or upload their details from a spreadsheet.',
              confirmationText: 'Continue',
              onConfirm: (dontShowAgainCheck) => {
                if (dontShowAgainCheck) {
                  setDialogHidden(
                    CampaignDialogKey.ATS_FOR_PASSIVE_CANDIDATES,
                    true
                  );
                }
                createCampaignFromCandidateList();
              }
            });
          }
        });
      }

      return options;
    }

    options.push(
      {
        label: 'Upload candidates',
        onClick: () => {
          if (
            campaignDialogStore[CampaignDialogKey.NON_ATS_UPLOAD_CANDIDATES]
          ) {
            track(TrackingEventName.CampaignsListCreateManually);
            createCampaign();
            return;
          }

          showModal({
            showDontShowAgainCheckbox: true,
            title: 'Upload candidates?',
            message: 'Add candidate details from a .csv file.',
            confirmationText: 'Continue',
            onConfirm: (dontShowAgainCheck) => {
              if (dontShowAgainCheck) {
                setDialogHidden(
                  CampaignDialogKey.NON_ATS_UPLOAD_CANDIDATES,
                  true
                );
              }
              track(TrackingEventName.CampaignsListCreateManually);
              createCampaign();
            }
          });
        }
      },
      {
        label: 'Generate link',
        onClick: () => {
          if (campaignDialogStore[CampaignDialogKey.NON_ATS_GENERATE_LINK]) {
            createCampaignFromAutomation();
            return;
          }

          showModal({
            showDontShowAgainCheckbox: true,
            title: 'Generate link?',
            message:
              'Share a link so candidates can apply directly via your website or job board.',
            confirmationText: 'Continue',
            onConfirm: (dontShowAgainCheck) => {
              if (dontShowAgainCheck) {
                setDialogHidden(CampaignDialogKey.NON_ATS_GENERATE_LINK, true);
              }
              createCampaignFromAutomation();
            }
          });
        }
      },
      {
        label: 'Connect to an analysis',
        onClick: () => {
          if (
            campaignDialogStore[CampaignDialogKey.NON_ATS_CONNECT_TO_ANALYSIS]
          ) {
            createCampaignFromAutomation();
            return;
          }

          showModal({
            showDontShowAgainCheckbox: true,
            title: 'Connect to an analysis?',
            message:
              'Create a campaign to message some or all applicants who have already been analysed.',
            confirmationText: 'Continue',
            onConfirm: (dontShowAgainCheck) => {
              if (dontShowAgainCheck) {
                setDialogHidden(
                  CampaignDialogKey.NON_ATS_CONNECT_TO_ANALYSIS,
                  true
                );
              }
              createCampaignFromAutomation();
            }
          });
        }
      }
    );

    return options;
  }, [campaignDialogStore, isConnected, isListsAvailable]);

  return (
    <ActionDropdown
      text="Create campaign"
      options={campaignCreationActionsOptions}
    />
  );
};
